import type {
  IntegrityStage,
  IntegrityState,
  MetricSource,
  PhysicalChannel,
  TransferPhase,
} from '@spectrabus/protocol';

export interface Metric<T> {
  readonly value: T;
  readonly unit: string;
  readonly source: MetricSource;
  readonly timestamp: number;
  readonly sampleCount: number;
}

export interface IntegrityCheck {
  readonly stage: IntegrityStage;
  readonly label: string;
  readonly state: IntegrityState;
  readonly detail: string;
}

export interface GenerationTelemetry {
  readonly generationId: number;
  readonly rank: number;
  readonly requiredRank: number;
  readonly opticalContributions: number;
  readonly acousticContributions: number;
  readonly verified: boolean;
}

export type CarrierModulation = 'OFF' | 'PILOT' | 'BPSK' | 'QPSK' | '16QAM';

export interface CarrierTelemetry {
  readonly index: number;
  readonly frequencyHz: number;
  readonly snrDb: number;
  readonly modulation: CarrierModulation;
  readonly enabled: boolean;
}

export interface SpatialRegionTelemetry {
  readonly index: number;
  readonly quality: number;
  readonly enabled: boolean;
}

export interface ChannelCounters {
  readonly accepted: number;
  readonly rejected: number;
  readonly duplicate: number;
  readonly independent: number;
}

export interface DemoIncident {
  readonly id: 'display-obstruction' | 'audio-interference';
  readonly label: string;
  readonly active: boolean;
  readonly resolved: boolean;
}

export interface HandshakeTelemetry {
  readonly protocol: 'SBHS/1';
  readonly keyAgreement: 'P-256 / HKDF-SHA-256';
  readonly transcriptHash: string;
  readonly transferId: string;
  readonly discoveryCommitment: string;
  readonly encodedOffer: string;
  readonly offerBytes: number;
  readonly responseBytes: number;
  readonly offerResponseBound: boolean;
  readonly confirmationBytes: number;
  readonly verifiedConfirmations: number;
  readonly requiredConfirmations: number;
  readonly mutuallyConfirmedChannels: readonly PhysicalChannel[];
  readonly transferAuthorised: boolean;
  readonly source: MetricSource;
  readonly transportSource: MetricSource;
}

export interface LinkSnapshot {
  readonly provenance: {
    readonly channel: MetricSource;
    readonly raster: MetricSource;
    readonly carrierPlan: MetricSource;
    readonly spatialQuality: MetricSource;
    readonly scheduler: MetricSource;
    readonly admissions: MetricSource;
    readonly handshake: MetricSource;
    readonly cryptography: MetricSource;
    readonly hashes: MetricSource;
  };
  readonly phase: TransferPhase;
  readonly phaseDetail: string;
  readonly handshake: HandshakeTelemetry;
  readonly sessionId: string;
  readonly sessionFingerprint: string;
  readonly sessionPublicKey: string;
  readonly manifestCommitment: string;
  readonly objectName: string;
  readonly objectMimeType: string;
  readonly objectBytes: number;
  readonly elapsedMs: number;
  readonly reconstructedFraction: number;
  readonly verifiedBytes: number;
  readonly totalIndependentSymbols: number;
  readonly requiredIndependentSymbols: number;
  readonly unresolvedSymbols: number;
  readonly integrityViolations: number;
  readonly opticalProfile: string;
  readonly acousticProfile: string;
  readonly opticalGoodput: Metric<number>;
  readonly acousticGoodput: Metric<number>;
  readonly aggregateGoodput: Metric<number>;
  readonly opticalRawBitrate: Metric<number>;
  readonly acousticRawBitrate: Metric<number>;
  readonly cameraFps: Metric<number | null>;
  readonly displayCadence: Metric<number>;
  readonly feedbackRtt: Metric<number>;
  readonly decoderLatency: Metric<number>;
  readonly fountainOverhead: Metric<number>;
  readonly duplicateRate: Metric<number>;
  readonly spatialUtilisation: Metric<number>;
  readonly environmentalStability: Metric<number>;
  readonly opticalAllocation: number;
  readonly acousticAllocation: number;
  readonly generations: readonly GenerationTelemetry[];
  readonly carriers: readonly CarrierTelemetry[];
  readonly spatialRegions: readonly SpatialRegionTelemetry[];
  readonly integrityChecks: readonly IntegrityCheck[];
  readonly incidents: readonly DemoIncident[];
  readonly counters: Readonly<Record<PhysicalChannel, ChannelCounters>>;
  readonly currentFrameCells: Uint8Array;
  readonly currentFrameWidth: number;
  readonly currentFrameHeight: number;
  readonly objectHash: string | null;
  readonly senderHash: string | null;
  readonly downloadBytes: Uint8Array | null;
  readonly failure: string | null;
}

export function metric<T>(
  value: T,
  unit: string,
  source: MetricSource,
  timestamp: number,
  sampleCount = 1,
): Metric<T> {
  return Object.freeze({ value, unit, source, timestamp, sampleCount });
}

export function provenanceLabel(source: MetricSource): string {
  switch (source) {
    case 'measured':
      return 'MEASURED';
    case 'derived':
      return 'DERIVED';
    case 'simulated':
      return 'SIMULATED';
    case 'unavailable':
      return 'UNAVAILABLE';
  }
}
